/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useIntl } from 'gatsby-plugin-react-intl';
import { ThemeProvider } from 'styled-components';

import Layout from '../../Components/Core/Layout';
import SEO from '../../Components/Core/Seo';
import BackToTop from '../../Components/Core/BackToTop';
import PageBuilder from '../../Components/Core/PageBuilder';
import BreadCrumbs from '../../Components/Custom/BreadCrumbs';
import pdfIconDam from '../../images/pdf-icon_green.svg';
import { heroBanner, proTip } from '../../utils/variation';
import { FeatureCards } from '../../Components/Custom/Card-Sections';
import Paragraph from '../../Components/Custom/Paragraph';
import { AccordionSection } from '../../Components/Custom/Sections';
import theme, { GlobalStyles } from '../../themes';
import { SpecialOffersBanner } from '../../Components/Custom/AppBanner';

import { StyledContainer, DownloadLink, StyledLegalPage } from './styles';

const pageTemplate = {
  'simple-paragraph': Paragraph,
  'legal-paragraph': Paragraph,
  ...proTip,
  'feature-block': FeatureCards,
  'accordion-section': AccordionSection,
  'special-offers': SpecialOffersBanner,
  'legal-stuff-paragraph': Paragraph,
  ...heroBanner,
};

const LegalArticlesTemplate = ({
  location,
  data: {
    contentfulLegalPageLayout: {
      languageToggle,
      pdfDownloadText,
      pdfAssetDownloadDam,
      customHeader,
      sections,
      customFooter,
      internal: { type },
      pageSlug,
    },
  },
}) => {
  const intl = useIntl();

  const ctaPdfLinkDam = pdfAssetDownloadDam;
  const iconPlacement = true;

  const data = { iconPlacement, ctaPdfLinkDam, pdfIconDam };

  return (
    <>
      {customHeader ? (
        <Layout
          location={location}
          header={customHeader}
          footer={customFooter}
          languageToggle={languageToggle}
          contentType={type}
        >
          <BreadCrumbs
            className="end-chevron"
            crumbs={[
              {
                label: `${intl.formatMessage({ id: 'LegalInfo' })}`,
                slug: '/legal-stuff',
                active: true,
              },
            ]}
          />
          {pdfAssetDownloadDam && pdfDownloadText && (
            <StyledContainer>
              <DownloadLink data={data}>{pdfDownloadText}</DownloadLink>
            </StyledContainer>
          )}
          <PageBuilder
            sections={sections}
            template={pageTemplate}
            pageSlug={pageSlug}
          />
          <BackToTop />
        </Layout>
      ) : (
        <ThemeProvider theme={theme}>
          <StyledLegalPage>
            <GlobalStyles />
            <PageBuilder
              sections={sections}
              template={pageTemplate}
              pageSlug={pageSlug}
              className="standalone"
            />
            <BackToTop />
          </StyledLegalPage>
        </ThemeProvider>
      )}
    </>
  );
};

LegalArticlesTemplate.propTypes = {
  location: PropTypes.shape().isRequired,
  data: PropTypes.shape().isRequired,
};

export const query = graphql`
  query LegalArticlesQuery($locale: String = "en-CA", $contentful_id: String!) {
    contentfulLegalPageLayout(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $locale }
    ) {
      node_locale
      pageTitle
      metaDescription
      languageToggle
      displayInSiteMap
      pageSlug
      internal {
        type
      }
      pdfDownloadText
      pdfAssetDownloadDam {
        asset {
          localImage {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: CONSTRAINED)
            }
          }
        }
      }
      ...global_legal_header
      sections {
        ...paragraph
        ...pro_tip
        ...legal_article_sections
        ...app_banner
      }
      ...global_legal_footer
    }
  }
`;

export default LegalArticlesTemplate;

export const Head = ({ location, data, pageContext }) => {
  return (
    <SEO
      data={data.contentfulLegalPageLayout}
      pageContext={pageContext}
      location={location}
    />
  );
};
